import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  AlertTriangle, CheckCircle2, Clock, FileText, Filter, XCircle,
  PauseCircle, Play, Eye, Edit3, Download, Plus, X, History, Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_ORDER = ['Critical', 'High', 'Medium', 'Low'];
const SEVERITY_STYLES = {
  Critical: { bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' },
  High:     { bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' },
  Medium:   { bg: '#E0F2FE', color: '#075985', border: '#7DD3FC' },
  Low:      { bg: '#F3F4F6', color: '#4B5563', border: '#D1D5DB' },
};
const STATUS_STYLES = {
  'Open':        { bg: '#FEE2E2', color: '#991B1B', icon: AlertTriangle },
  'In Progress': { bg: '#FEF3C7', color: '#92400E', icon: Play },
  'Fixed':       { bg: '#D1FAE5', color: '#065F46', icon: CheckCircle2 },
  'Verified':    { bg: '#E0F2FE', color: '#075985', icon: CheckCircle2 },
  'Deferred':    { bg: '#F3F4F6', color: '#4B5563', icon: PauseCircle },
  "Won't Fix":   { bg: '#F3F4F6', color: '#4B5563', icon: XCircle },
};
const CATEGORIES = [
  'Typography', 'Mobile', 'Favicon', 'Email', 'Security', 'Pricing',
  'Onboarding', 'Theme', 'Command Centre', 'SOC2/BIG5', 'Copy',
  'Branding', 'Legal', 'Charter', 'Lint', 'Other',
];

const getToken = () =>
  localStorage.getItem('hibff_auth_token') ||
  sessionStorage.getItem('hibff_auth_token') || '';

const auth = () => ({ 'Authorization': `Bearer ${getToken()}` });

// ── Founder Notes Archive · hover-trail cache ────────────────────────────
// Module-scoped Map<issue_id, {trail, fetchedAt}> so a founder can hover
// the same row multiple times a session without re-fetching. Trails are
// tiny (typically 3-8 audit rows) so the cache is essentially free.
const _trailCache = new Map();
const TRAIL_TTL_MS = 60_000; // 60s — plenty for a Command Centre browse session

function _timeAgoShort(iso) {
  if (!iso) return '';
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  } catch (_) { return ''; }
}

function _summariseAudit(evt) {
  const { action, before = {}, after = {} } = evt || {};
  if (action === 'created') {
    return `raised · ${after.severity || ''} · ${after.category || ''}`.trim();
  }
  if (action === 'verified') {
    return `verified by ${after.verified_by || evt.by}`;
  }
  if (action === 'updated') {
    const changes = [];
    for (const k of ['status', 'severity', 'category', 'assigned_to', 'outcome', 'follow_on_actions']) {
      if (after[k] !== undefined && after[k] !== before[k]) {
        const val = String(after[k] ?? '').trim().slice(0, 60);
        if (!val) continue;
        changes.push(`${k.replace(/_/g, ' ')} → ${val}`);
      }
    }
    return changes.length ? changes.join(' · ') : 'updated';
  }
  return action || 'event';
}

const IssueTrailTooltip = ({ issueId, anchorRef, onClose }) => {
  const [state, setState] = useState({ loading: true, trail: null, err: null });
  const [style, setStyle] = useState({ display: 'none' });
  const tipRef = useRef(null);

  useEffect(() => {
    const cached = _trailCache.get(issueId);
    if (cached && (Date.now() - cached.fetchedAt) < TRAIL_TTL_MS) {
      setState({ loading: false, trail: cached.trail, err: null });
      return () => {};
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/stabilisation/${issueId}`, { headers: auth() });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        const trail = (j.audit || []).slice(0, 8);
        _trailCache.set(issueId, { trail, fetchedAt: Date.now() });
        if (!cancelled) setState({ loading: false, trail, err: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, trail: null, err: e.message || 'load failed' });
      }
    })();
    return () => { cancelled = true; };
  }, [issueId]);

  // Position tooltip next to the anchor row. Chooses right-side by default,
  // flips to left if right would overflow the viewport.
  useEffect(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const tipW = 360;
    const tipH = 240;
    let left = rect.right + 12;
    if (left + tipW > window.innerWidth - 12) {
      left = Math.max(12, rect.left - tipW - 12);
    }
    let top = rect.top;
    if (top + tipH > window.innerHeight - 12) {
      top = Math.max(12, window.innerHeight - tipH - 12);
    }
    setStyle({ position: 'fixed', top: `${top}px`, left: `${left}px`, width: `${tipW}px`, zIndex: 60 });
  }, [anchorRef, state.loading]);

  return (
    <div
      ref={tipRef}
      style={style}
      onMouseEnter={() => { /* keep visible when cursor moves onto tooltip */ }}
      onMouseLeave={onClose}
      className="bg-white rounded-xl shadow-2xl border border-indigo-200 p-3 text-xs"
      data-testid={`stab-trail-${issueId}`}
    >
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100">
        <div className="flex items-center gap-1.5 text-indigo-700 font-bold uppercase tracking-wider text-[10px]">
          <History size={11} /> Founder notes · {issueId}
        </div>
        <span className="text-[9px] text-gray-400 uppercase tracking-wider">Hover trail</span>
      </div>
      {state.loading && (
        <div className="flex items-center gap-2 py-4 text-gray-500">
          <Loader2 size={14} className="animate-spin" /> Loading trail…
        </div>
      )}
      {state.err && (
        <p className="py-3 text-red-700 text-[11px]">Could not load trail: {state.err}</p>
      )}
      {state.trail && state.trail.length === 0 && (
        <p className="py-3 text-gray-500 italic">No notes recorded yet.</p>
      )}
      {state.trail && state.trail.length > 0 && (
        <ol className="relative border-l border-indigo-200 ml-2 space-y-2.5">
          {state.trail.map((evt, i) => (
            <li key={`${evt.at}-${i}`} className="pl-3 relative" data-testid={`stab-trail-${issueId}-evt-${i}`}>
              <span className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-indigo-500 border-2 border-white shadow-sm" />
              <p className="text-[11px] text-gray-900 font-semibold leading-tight">{_summariseAudit(evt)}</p>
              <p className="text-[10px] text-gray-500 leading-tight mt-0.5">
                <span className="font-mono">{(evt.by || 'system').split('@')[0]}</span>
                <span className="text-gray-400"> · {_timeAgoShort(evt.at)} ago</span>
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

// Module-scoped so it's a stable component reference across parent re-renders.
// Previously defined inside StabilisationLog, which meant React remounted the
// entire counts row every time the parent state changed (loading, filters,
// polling) — visible as a small flicker + wasted DOM work.
const HeaderCounts = ({ stats }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="stab-counts">
    {SEVERITY_ORDER.map(sev => {
      const n = stats?.by_severity?.[sev] || 0;
      const sty = SEVERITY_STYLES[sev];
      return (
        <div key={sev}
             className="rounded-lg border p-3 flex items-center gap-3"
             style={{ backgroundColor: sty.bg, borderColor: sty.border }}
             data-testid={`stab-count-${sev.toLowerCase()}`}>
          <AlertTriangle size={18} style={{ color: sty.color }} />
          <div>
            <p className="text-xs font-semibold" style={{ color: sty.color }}>{sev}</p>
            <p className="text-2xl font-black" style={{ color: sty.color }}>{n}</p>
          </div>
        </div>
      );
    })}
  </div>
);

export const StabilisationLog = ({ compact = false }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterSev, setFilterSev] = useState('');
  const [filterStatus, setFilterStatus] = useState('Open');
  const [filterCat, setFilterCat] = useState('');
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  // Founder Notes hover-trail state — records which issue_id is currently
  // being hovered so a floating trail tooltip renders next to that row.
  const [hoveredIssue, setHoveredIssue] = useState(null);
  const hoverAnchorRef = useRef(null);
  const hoverTimerRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (filterSev) qs.set('severity', filterSev);
      if (filterStatus) qs.set('status', filterStatus);
      if (filterCat) qs.set('category', filterCat);

      const [listRes, statsRes] = await Promise.all([
        fetch(`${API}/api/admin/stabilisation/list?${qs}`, { headers: auth() }),
        fetch(`${API}/api/admin/stabilisation/stats`, { headers: auth() }),
      ]);
      if (!listRes.ok) throw new Error(`List: HTTP ${listRes.status}`);
      if (!statsRes.ok) throw new Error(`Stats: HTTP ${statsRes.status}`);
      const listJson = await listRes.json();
      const statsJson = await statsRes.json();
      setItems(listJson.items || []);
      setStats(statsJson);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filterSev, filterStatus, filterCat]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const downloadCsv = async () => {
    const res = await fetch(`${API}/api/admin/stabilisation/export/csv`, { headers: auth() });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    a.href = url;
    a.download = `HiBFF-Stabilisation-${stamp}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const sevDiff = SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity);
      if (sevDiff !== 0) return sevDiff;
      return a.issue_id.localeCompare(b.issue_id);
    });
  }, [items]);

  if (loading && !stats) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse" style={{ minHeight: 200 }}>
        <div className="h-6 bg-gray-200 rounded w-64 mb-4" />
        <div className="h-20 bg-gray-100 rounded mb-3" />
        <div className="h-20 bg-gray-100 rounded" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <p className="text-sm font-semibold text-red-800">Could not load Stabilisation log</p>
        <p className="text-xs text-red-700 mt-1">{error}</p>
        <button onClick={fetchAll}
                className="mt-3 text-xs font-bold text-red-700 underline"
                data-testid="stab-retry">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="stabilisation-log">
      {/* Header */}
      <div className="p-5 border-b border-gray-200 flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h2 className="text-xl font-black text-gray-900 flex items-center gap-2">
            <FileText size={20} className="text-orange-600" />
            Stabilisation
          </h2>
          <p className="text-xs text-gray-600 mt-1">
            Running register of production issues, follow-on actions and outcomes.
            {stats && ` ${stats.total} total · ${stats.by_status.Open} open · ${stats.by_status.Fixed + stats.by_status.Verified} closed.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={downloadCsv}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center gap-1.5"
                  data-testid="stab-export-csv">
            <Download size={12} /> CSV
          </button>
          <button onClick={() => setShowNew(true)}
                  className="text-xs font-bold px-3 py-1.5 rounded-lg text-white bg-orange-600 hover:bg-orange-700 flex items-center gap-1.5"
                  data-testid="stab-new">
            <Plus size={12} /> Raise issue
          </button>
        </div>
      </div>

      {/* Counts */}
      <div className="p-5 pb-0">
        <HeaderCounts stats={stats} />
      </div>

      {/* Filters */}
      <div className="px-5 pb-3 flex flex-wrap gap-2 items-center">
        <Filter size={14} className="text-gray-500" />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                className="text-xs border border-gray-300 rounded-lg px-2 py-1"
                data-testid="stab-filter-status">
          <option value="">All statuses</option>
          {Object.keys(STATUS_STYLES).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterSev} onChange={e => setFilterSev(e.target.value)}
                className="text-xs border border-gray-300 rounded-lg px-2 py-1"
                data-testid="stab-filter-sev">
          <option value="">All severities</option>
          {SEVERITY_ORDER.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
                className="text-xs border border-gray-300 rounded-lg px-2 py-1"
                data-testid="stab-filter-cat">
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-gray-500 ml-2">{sortedItems.length} visible</span>
      </div>

      {/* List */}
      <div className={`${compact ? 'max-h-96' : 'max-h-[680px]'} overflow-y-auto border-t border-gray-100`}>
        {sortedItems.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">No issues match the current filters.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {sortedItems.map(item => {
              const sevSty = SEVERITY_STYLES[item.severity] || SEVERITY_STYLES.Low;
              const stSty = STATUS_STYLES[item.status] || STATUS_STYLES.Open;
              const StIcon = stSty.icon;
              return (
                <li key={item.issue_id}
                    className="px-5 py-3 hover:bg-gray-50 transition-colors cursor-pointer relative"
                    onClick={() => setSelected(item)}
                    onMouseEnter={(e) => {
                      const target = e.currentTarget;
                      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                      // 400ms open delay so casual pointer-fly-overs don't trigger a fetch.
                      hoverTimerRef.current = setTimeout(() => {
                        hoverAnchorRef.current = target;
                        setHoveredIssue(item.issue_id);
                      }, 400);
                    }}
                    onMouseLeave={() => {
                      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                      // 200ms grace so mousing from row → tooltip doesn't clip it.
                      hoverTimerRef.current = setTimeout(() => setHoveredIssue(null), 200);
                    }}
                    data-testid={`stab-row-${item.issue_id}`}>
                  <div className="flex items-start gap-3">
                    <span className="font-mono text-[11px] font-bold text-gray-700 pt-0.5" style={{ minWidth: 44 }}>
                      {item.issue_id}
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: sevSty.bg, color: sevSty.color }}>
                      {item.severity}
                    </span>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded flex-shrink-0 bg-gray-100 text-gray-700">
                      {item.category}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 truncate">{item.issue_text}</p>
                      {item.outcome && (
                        <p className="text-xs text-green-700 truncate mt-0.5">✓ {item.outcome}</p>
                      )}
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0"
                          style={{ backgroundColor: stSty.bg, color: stSty.color }}>
                      <StIcon size={10} />
                      {item.status}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {hoveredIssue && (
        <IssueTrailTooltip
          issueId={hoveredIssue}
          anchorRef={hoverAnchorRef}
          onClose={() => setHoveredIssue(null)}
        />
      )}

      {selected && (
        <IssueDetailModal
          issue={selected}
          onClose={() => setSelected(null)}
          onUpdated={() => { setSelected(null); fetchAll(); }}
        />
      )}

      {showNew && (
        <NewIssueModal
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); fetchAll(); }}
        />
      )}
    </div>
  );
};

// ── Detail Modal (view + inline edit) ──

const IssueDetailModal = ({ issue, onClose, onUpdated }) => {
  const [status, setStatus] = useState(issue.status);
  const [followOn, setFollowOn] = useState(issue.follow_on_actions || '');
  const [outcome, setOutcome] = useState(issue.outcome || '');
  const [assigned, setAssigned] = useState(issue.assigned_to || '');
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await fetch(`${API}/api/admin/stabilisation/${issue.issue_id}`, { headers: auth() });
      if (r.ok) {
        const j = await r.json();
        setAudit(j.audit || []);
      }
    })();
  }, [issue.issue_id]);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/admin/stabilisation/${issue.issue_id}`, {
        method: 'PATCH',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          follow_on_actions: followOn,
          outcome,
          assigned_to: assigned || null,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      onUpdated();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => {
    setSaving(true); setErr(null);
    try {
      const name = prompt('Your name for sign-off?');
      if (!name) { setSaving(false); return; }
      const r = await fetch(`${API}/api/admin/stabilisation/${issue.issue_id}/verify`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ verified_by: name }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        throw new Error(b.detail || `HTTP ${r.status}`);
      }
      onUpdated();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="stab-detail-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm font-bold text-gray-800">{issue.issue_id}</span>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: SEVERITY_STYLES[issue.severity]?.bg,
                    color: SEVERITY_STYLES[issue.severity]?.color,
                  }}>
              {issue.severity}
            </span>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-700">
              {issue.category}
            </span>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" data-testid="stab-detail-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1">Issue</p>
            <p className="text-sm text-gray-900">{issue.issue_text}</p>
            {issue.source_ref && (
              <p className="text-xs text-gray-500 mt-1">Source: {issue.source_ref}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Raised by {issue.raised_by} · {new Date(issue.raised_at).toLocaleDateString()}
            </p>
          </div>

          <div>
            <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}
                    className="w-full mt-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
                    data-testid="stab-detail-status">
              {Object.keys(STATUS_STYLES).filter(s => s !== 'Verified').map(s =>
                <option key={s} value={s}>{s}</option>
              )}
              {issue.status === 'Verified' && <option value="Verified">Verified</option>}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">Assigned to</label>
            <input value={assigned} onChange={e => setAssigned(e.target.value)}
                   placeholder="e.g. Emergent agent"
                   className="w-full mt-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
                   data-testid="stab-detail-assigned" />
          </div>

          <div>
            <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">Follow-on actions</label>
            <textarea value={followOn} onChange={e => setFollowOn(e.target.value)}
                      rows={4}
                      placeholder="Running notes: files touched, decisions, blockers…"
                      className="w-full mt-1 text-sm border border-gray-300 rounded-lg px-3 py-2 font-mono"
                      data-testid="stab-detail-followon" />
          </div>

          <div>
            <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">Outcome</label>
            <textarea value={outcome} onChange={e => setOutcome(e.target.value)}
                      rows={3}
                      placeholder="Final fix summary once the item is complete"
                      className="w-full mt-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
                      data-testid="stab-detail-outcome" />
          </div>

          {err && <p className="text-xs text-red-700 font-semibold">{err}</p>}

          <div className="flex flex-wrap gap-2 pt-2">
            <button onClick={save} disabled={saving}
                    className="text-xs font-bold px-4 py-2 rounded-lg text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
                    data-testid="stab-detail-save">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {['Fixed', 'Deferred', "Won't Fix"].includes(issue.status) && issue.status !== 'Verified' && (
              <button onClick={verify} disabled={saving}
                      className="text-xs font-bold px-4 py-2 rounded-lg border border-green-500 text-green-700 hover:bg-green-50"
                      data-testid="stab-detail-verify">
                Sign off as Verified
              </button>
            )}
          </div>

          {audit.length > 0 && (
            <div className="pt-4 border-t border-gray-100">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Clock size={12} /> History
              </p>
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {audit.map((a, idx) => (
                  <li key={idx} className="text-xs text-gray-700 font-mono">
                    <span className="text-gray-500">{new Date(a.at).toLocaleString()}</span>
                    {' · '}<span className="font-bold">{a.action}</span>
                    {' · '}{a.by}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── New Issue Modal ──

const NewIssueModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    raised_by: '', category: 'Other', severity: 'Medium', issue_text: '', source_ref: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!form.raised_by || !form.issue_text) {
      setErr('Name + issue text are required');
      return;
    }
    setSubmitting(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/admin/stabilisation/create`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        throw new Error(b.detail || `HTTP ${r.status}`);
      }
      onCreated();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="stab-new-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full"
           onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base font-black text-gray-900">Raise a new issue</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" data-testid="stab-new-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Raised by</span>
            <input value={form.raised_by}
                   onChange={e => setForm(f => ({ ...f, raised_by: e.target.value }))}
                   placeholder="Your name"
                   className="w-full mt-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
                   data-testid="stab-new-raised-by" />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Category</span>
              <select value={form.category}
                      onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                      className="w-full mt-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
                      data-testid="stab-new-category">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Severity</span>
              <select value={form.severity}
                      onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                      className="w-full mt-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
                      data-testid="stab-new-severity">
                {SEVERITY_ORDER.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Description</span>
            <textarea value={form.issue_text}
                      onChange={e => setForm(f => ({ ...f, issue_text: e.target.value }))}
                      rows={4}
                      placeholder="Describe what you observed, where, and impact"
                      className="w-full mt-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
                      data-testid="stab-new-text" />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Source / URL (optional)</span>
            <input value={form.source_ref}
                   onChange={e => setForm(f => ({ ...f, source_ref: e.target.value }))}
                   placeholder="e.g. hibff.com/login or stabilisation doc #X"
                   className="w-full mt-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
                   data-testid="stab-new-source" />
          </label>
          {err && <p className="text-xs text-red-700 font-semibold">{err}</p>}
          <button onClick={submit} disabled={submitting}
                  className="w-full text-sm font-bold px-4 py-2.5 rounded-lg text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
                  data-testid="stab-new-submit">
            {submitting ? 'Creating…' : 'Create issue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StabilisationLog;
